import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminGetIssuer, adminSaveIssuer, formatApiError } from "@/lib/api";
import { StateBadge } from "./IntegrationsPanel";

const FIELDS = [
  ["legal_name", "Ragione sociale / denominazione emittente"], ["vat_number", "Partita IVA"], ["tax_code", "Codice fiscale"],
  ["address", "Indirizzo sede"], ["postal_code", "CAP"], ["city", "Città"], ["province", "Provincia (sigla)"], ["country", "Nazione (es. IT)"],
  ["tax_regime", "Regime fiscale (codice RF, es. RF01)"], ["vat_rate", "Aliquota IVA % (es. 22)"],
  ["numbering_prefix", "Prefisso numerazione fatture (opzionale)"], ["legal_notes", "Causale / note legali in fattura (opzionale)"],
];
const SRC = { admin: "impostato dal pannello", env: "da Production Secrets", missing: "mancante" };

export function IssuerForm() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => adminGetIssuer().then((r) => { setD(r); setForm(Object.fromEntries(Object.entries(r.issuer).map(([k, v]) => [k, v || ""]))); }).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try { const r = await adminSaveIssuer(form); setD(r); toast.success("Dati fiscali emittente salvati"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  if (!d) return <div className="text-sm text-slate-500">Caricamento…</div>;
  return (
    <div className="nadia-card p-5 space-y-4" data-testid="admin-issuer-form">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="font-heading text-base font-semibold text-slate-100">Profilo fiscale emittente Nadia Cloud</div>
        <StateBadge state={d.state} />
      </div>
      {!d.issuer_ready && (
        <div className="text-xs text-amber-300" data-testid="admin-issuer-warning">
          Configurazione fatturazione necessaria: compila {d.issuer_missing.map((k) => FIELDS.find((f) => f[0] === k)?.[1] || k).join(", ")}.
        </div>
      )}
      {!d.provider_ready && (
        <div className="text-xs text-slate-400" data-testid="admin-aruba-warning">
          Credenziali Aruba Fatturazione Elettronica mancanti nei Production Secrets: <span className="font-mono">{d.provider_missing_env.join(", ")}</span>. Le fatture vengono generate ma non inviate.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k} className="space-y-1">
            <label className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
              {label}{d.required.includes(k) && <span className="text-rose-300">*</span>}
              <span className="text-slate-600 normal-case tracking-normal font-normal">· {SRC[d.sources[k]]}</span>
            </label>
            <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-[#0B1120] border-white/10 text-slate-100" data-testid={`issuer-${k}`} />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-500">Il valore inserito qui prevale sulla variabile d'ambiente corrispondente. Nessun dato viene precompilato.</p>
      <Button onClick={save} disabled={saving} className="bg-cyan-500 text-slate-950 hover:bg-cyan-400" data-testid="issuer-save">
        {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />} Salva dati emittente
      </Button>
    </div>
  );
}
